import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import chatApi from '../../api/chat';

const ChatWindow = ({ bookingId }) => {
  const { user } = useAuth();
  const { 
    messages, 
    setMessages, 
    joinConversation, 
    leaveConversation, 
    sendTyping, 
    isTyping,
    activeConversation,
    setActiveConversation
  } = useChat();
  
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const initChat = async () => {
      try {
        setLoading(true);
        const conversation = await chatApi.getOrCreateConversation(bookingId);
        setActiveConversation(conversation);
        joinConversation(conversation);
        
        const history = await chatApi.getMessages(conversation.id);
        setMessages(history);
      } catch (error) {
        console.error('Failed to initialize chat:', error);
      } finally {
        setLoading(false);
      }
    };

    if (bookingId) {
      initChat();
    }

    return () => {
      if (activeConversation) {
        leaveConversation(activeConversation.id);
      }
    };
  }, [bookingId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    try {
      const msgText = newMessage;
      setNewMessage('');
      sendTyping(false);
      await chatApi.sendMessage(activeConversation.id, msgText);
      // Message will be added via socket event 'new_message'
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    if (e.target.value.length > 0) {
      sendTyping(true);
    } else {
      sendTyping(false);
    }
  };

  if (loading) return <div className="p-4 text-center">Loading chat...</div>;

  return (
    <div className="flex flex-col h-[500px] border rounded-lg bg-white shadow-sm">
      <div className="p-4 border-b bg-gray-50 rounded-t-lg">
        <h3 className="font-semibold text-gray-800">Chat with {user.role === 'customer' ? 'Business Owner' : 'Customer'}</h3>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => (
          <div 
            key={msg.id} 
            className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
          >
            <div 
              className={`max-w-[70%] px-4 py-2 rounded-lg ${
                msg.sender_id === user.id 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              <p className="text-sm">{msg.message_text}</p>
              <span className="text-[10px] opacity-70 block mt-1">
                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="text-xs text-gray-500 italic">
            Someone is typing...
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="p-4 border-t">
        <div className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={handleTyping}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
          >
            Send
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatWindow;
